import { useMemo, useState } from "react";
import {
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { AppText } from "./AppText";
import { Button } from "./Button";
import { ButtonText } from "./ButtonText";
import {
  fonts,
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import {
  filterSelectOptions,
  type SelectOption,
} from "~/utils/regional-options";

export function SearchableSelect({
  value,
  options,
  onValueChange,
  title,
  placeholder = "Select an option",
  searchPlaceholder = "Search options",
  accessibilityLabel = title,
  disabled = false,
}: {
  value: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
  title: string;
  placeholder?: string;
  searchPlaceholder?: string;
  accessibilityLabel?: string;
  disabled?: boolean;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((option) => option.value === value);
  const filtered = useMemo(
    () => filterSelectOptions(options, query),
    [options, query],
  );

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled }}
        accessibilityHint={`Opens a searchable list of ${title.toLowerCase()}`}
        disabled={disabled}
        onPress={() => setOpen(true)}
        style={({ hovered, pressed }) => [
          stylesheet.trigger,
          hovered || pressed ? stylesheet.triggerHover : null,
          disabled ? stylesheet.triggerDisabled : null,
        ]}
      >
        <Text
          numberOfLines={1}
          style={[
            stylesheet.triggerText,
            selected ? null : stylesheet.placeholder,
          ]}
        >
          {selected?.label ?? placeholder}
        </Text>
        <Text style={stylesheet.chevron}>v</Text>
      </Pressable>

      <Modal
        animationType="fade"
        transparent
        visible={open}
        onRequestClose={close}
      >
        <View style={stylesheet.overlay}>
          <View style={stylesheet.card}>
            <View style={stylesheet.header}>
              <AppText size="lg" weight="bold">
                {title}
              </AppText>
              <Button variant="outline" compact onPress={close}>
                <ButtonText>Close</ButtonText>
              </Button>
            </View>
            <TextInput
              autoFocus
              accessibilityLabel={`Search ${title.toLowerCase()}`}
              placeholder={searchPlaceholder}
              value={query}
              onChangeText={setQuery}
              onKeyPress={(event) => {
                if (event.nativeEvent.key === "Escape") close();
              }}
              style={stylesheet.input}
            />
            <FlatList
              data={filtered}
              keyExtractor={(option) => option.value}
              keyboardShouldPersistTaps="handled"
              style={stylesheet.list}
              ListEmptyComponent={
                <AppText size="xs">No matching options.</AppText>
              }
              renderItem={({ item }) => {
                const active = item.value === value;
                return (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => {
                      onValueChange(item.value);
                      close();
                    }}
                    style={({ hovered, pressed }) => [
                      stylesheet.option,
                      active ? stylesheet.optionActive : null,
                      hovered || pressed ? stylesheet.optionHover : null,
                    ]}
                  >
                    <Text style={stylesheet.optionLabel}>{item.label}</Text>
                  </Pressable>
                );
              }}
            />
          </View>
        </View>
      </Modal>
    </>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    trigger: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: 10,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    triggerHover: { backgroundColor: colors.surface },
    triggerDisabled: { opacity: 0.6 },
    triggerText: {
      flex: 1,
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 14,
    },
    placeholder: { color: colors.mutedForeground },
    chevron: {
      color: colors.mutedForeground,
      fontFamily: fonts.regular,
      fontSize: 18,
    },
    overlay: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
      backgroundColor: colors.overlay,
    },
    card: {
      width: "100%",
      maxWidth: 560,
      maxHeight: "80%",
      padding: 20,
      borderRadius: radii.l,
      backgroundColor: colors.background,
      gap: spacing.m,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
    },
    input: {
      minHeight: 44,
      padding: 10,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      color: colors.foreground,
      fontFamily: fonts.regular,
    },
    list: { minHeight: 120 },
    option: {
      minHeight: 44,
      justifyContent: "center",
      paddingHorizontal: 10,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    optionActive: { backgroundColor: colors.surface },
    optionHover: { backgroundColor: colors.mutedSurface },
    optionLabel: {
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 14,
    },
  });
